import api from '../utils/api'

const contactService = {
  // Get all contacts with pagination and filters
  getAllContacts: async (params = {}) => {
    try {
      const response = await api.get('/api/contacts', { params })
      return response.data
    } catch (error) {
      console.error('Error fetching contacts:', error)
      throw error
    }
  },

  // Get customers for dropdown
  getCustomersForDropdown: async () => {
    try {
      const response = await api.get('/api/contacts/customers/dropdown')
      return response.data
    } catch (error) {
      console.error('Error fetching customers dropdown:', error)
      throw error
    }
  },

  // Get suppliers for dropdown
  getSuppliersForDropdown: async () => {
    try {
      const response = await api.get('/api/contacts/suppliers/dropdown')
      return response.data
    } catch (error) {
      console.error('Error fetching suppliers dropdown:', error)
      throw error
    }
  },

  // Get contact by ID
  getContactById: async (id) => {
    try {
      const response = await api.get(`/api/contacts/${id}`)
      return response.data.data
    } catch (error) {
      console.error('Error fetching contact:', error)
      throw error
    }
  },

  // Get contact transactions
  getContactTransactions: async (id, params = {}) => {
    try {
      const response = await api.get(`/api/contacts/${id}/transactions`, { params })
      return response.data
    } catch (error) {
      console.error('Error fetching contact transactions:', error)
      throw error
    }
  },

  // Get insurance expiring soon
  getExpiringInsurance: async (days = 30) => {
    try {
      const response = await api.get('/api/contacts/insurance/expiring', { 
        params: { days } 
      })
      return response.data
    } catch (error) {
      console.error('Error fetching expiring insurance:', error)
      throw error
    }
  },

  // Create new contact
  createContact: async (contactData) => {
    try {
      const response = await api.post('/api/contacts', contactData)
      return response.data
    } catch (error) {
      console.error('Error creating contact:', error)
      throw error
    }
  },

  // Update contact
  updateContact: async (id, contactData) => {
    try {
      const response = await api.put(`/api/contacts/${id}`, contactData)
      return response.data
    } catch (error) {
      console.error('Error updating contact:', error)
      throw error
    }
  },

  // Renew insurance
  renewInsurance: async (id, renewalData) => {
    try {
      const response = await api.put(`/api/contacts/${id}/insurance/renew`, renewalData)
      return response.data
    } catch (error) {
      console.error('Error renewing insurance:', error)
      throw error
    }
  },

  // Delete contact (soft delete)
  deleteContact: async (id) => {
    try {
      const response = await api.delete(`/api/contacts/${id}`)
      return response.data
    } catch (error) {
      console.error('Error deleting contact:', error)
      throw error
    }
  },

  // Utility functions for insurance status
  calculateInsuranceStatus: (startDate, endDate) => {
    if (!startDate || !endDate) return 'not_applicable'
    
    const today = new Date()
    const end = new Date(endDate)
    const diffTime = end.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays < 0) return 'expired'
    if (diffDays <= 30) return 'expiring_soon'
    return 'active'
  },

  // Get insurance status display config
  getInsuranceStatusConfig: (status) => {
    const statusConfigs = {
      'active': {
        label: 'ใช้งานได้',
        color: 'green',
        priority: 1
      },
      'expiring_soon': {
        label: 'ใกล้หมดอายุ',
        color: 'orange',
        priority: 2
      },
      'expired': {
        label: 'หมดอายุแล้ว',
        color: 'red',
        priority: 3
      },
      'not_applicable': {
        label: 'ไม่มีประกัน',
        color: 'gray',
        priority: 4
      }
    }
    
    return statusConfigs[status] || statusConfigs['not_applicable']
  },

  // Calculate days until expiry
  calculateDaysUntilExpiry: (endDate) => {
    if (!endDate) return null
    
    const today = new Date()
    const expiry = new Date(endDate)
    const diffTime = expiry.getTime() - today.getTime()
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  },

  // Validate insurance dates
  validateInsuranceDates: (startDate, endDate) => {
    if (!startDate || !endDate) return true
    
    const start = new Date(startDate)
    const end = new Date(endDate)
    
    if (start >= end) {
      throw new Error('วันหมดอายุต้องมาหลังวันเริ่มต้น')
    }
    
    return true
  },

  // Format insurance amount for display
  formatInsuranceAmount: (amount) => {
    if (!amount || amount === 0) return '-'
    return `${amount.toLocaleString()} ฿`
  },

  // Get insurance status summary for dashboard
  getInsuranceStatusSummary: (contacts) => {
    const summary = {
      total: 0,
      active: 0,
      expiring_soon: 0,
      expired: 0,
      not_applicable: 0
    }

    contacts.forEach(contact => {
      if (contact.type === 'supplier') return
      
      summary.total++
      const status = contact.calculated_insurance_status || contact.insurance_status
      summary[status] = (summary[status] || 0) + 1
    })

    return summary
  },

  // Get upcoming renewals for notifications
  getUpcomingRenewals: (contacts, days = 30) => {
    return contacts.filter(contact => {
      if (contact.type === 'supplier' || !contact.insurance_end_date) return false
      
      const daysUntilExpiry = contactService.calculateDaysUntilExpiry(contact.insurance_end_date)
      return daysUntilExpiry > 0 && daysUntilExpiry <= days
    }).sort((a, b) => {
      const daysA = contactService.calculateDaysUntilExpiry(a.insurance_end_date)
      const daysB = contactService.calculateDaysUntilExpiry(b.insurance_end_date)
      return daysA - daysB
    })
  },

  // Export insurance data for reporting
  exportInsuranceData: (contacts) => {
    return contacts.filter(contact => contact.type !== 'supplier')
      .map(contact => ({
        name: contact.name,
        company_name: contact.company_name,
        phone: contact.phone,
        email: contact.email,
        insurance_start_date: contact.insurance_start_date,
        insurance_end_date: contact.insurance_end_date,
        insurance_renewal_amount: contact.insurance_renewal_amount,
        insurance_status: contact.calculated_insurance_status || contact.insurance_status,
        days_until_expiry: contactService.calculateDaysUntilExpiry(contact.insurance_end_date)
      }))
  }
}

// Named exports for easier import
export const getContacts = contactService.getAllContacts
export const getContactById = contactService.getContactById
export const createContact = contactService.createContact
export const updateContact = contactService.updateContact
export const deleteContact = contactService.deleteContact
export const getContactTransactions = contactService.getContactTransactions
export const getCustomersForDropdown = contactService.getCustomersForDropdown
export const getSuppliersForDropdown = contactService.getSuppliersForDropdown

export default contactService
